import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { catchError, of, switchMap } from 'rxjs';
import { OAuthAuthorization, OAuthAuthorizationsResponse } from 'src/app/models/oauth-authorization.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { AuthService } from 'src/app/services/auth.service';

const SCOPE_LABELS: Record<string, string> = {
  'mcp:use': 'Usar el servidor MCP (agente / IDE)',
  'mail:read': 'Leer el buzón de correo (IMAP)',
};

@Component({
  selector: 'app-user-detail-oauth-section',
  templateUrl: './user-detail-oauth-section.component.html',
  styleUrl: './user-detail-oauth-section.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButton, MatIcon, DatePipe],
})
export class UserDetailOauthSectionComponent {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);

  readonly user_id = input.required<string>();
  readonly user = input.required<User>();
  readonly its_me = input.required<boolean>();

  private readonly refresh_tick = signal(0);
  readonly revoking_client_id = signal<string | null>(null);
  readonly logging_out = signal(false);

  readonly can_manage = computed((): boolean => this.its_me());

  readonly authorizations_response = toSignal(
    toObservable(this.refresh_tick).pipe(
      switchMap(() =>
        this.api.getMyOAuthAuthorizations().pipe(
          catchError(() =>
            of({
              data: [] as OAuthAuthorization[],
              has_mail_read: false,
              mail_reauthorize_hint: '',
            } satisfies OAuthAuthorizationsResponse)
          )
        )
      )
    ),
    {
      initialValue: {
        data: [] as OAuthAuthorization[],
        has_mail_read: false,
        mail_reauthorize_hint: '',
      } satisfies OAuthAuthorizationsResponse,
    }
  );

  readonly applications = computed((): OAuthAuthorization[] => this.authorizations_response().data);

  readonly scopes = computed((): OAuthScopeView[] => {
    const applications = this.applications();
    const granted_scope_ids = new Set(applications.flatMap((application: OAuthAuthorization): string[] => application.scopes));
    const catalog_scope_ids = [...new Set([...Object.keys(SCOPE_LABELS), ...granted_scope_ids])].sort();

    return catalog_scope_ids.map((scope_id: string): OAuthScopeView => {
      const apps_with_scope = applications.filter((application: OAuthAuthorization): boolean => application.scopes.includes(scope_id));
      const is_granted = apps_with_scope.length > 0;

      return {
        scope_id,
        label: SCOPE_LABELS[scope_id] ?? scope_id,
        is_granted,
        application_names_label: is_granted
          ? apps_with_scope.map((application: OAuthAuthorization): string => application.client_name).join(', ')
          : null,
      };
    });
  });

  readonly mail_reauthorize_hint = computed((): string => this.authorizations_response().mail_reauthorize_hint);
  readonly has_mail_read = computed((): boolean => this.authorizations_response().has_mail_read);
  readonly show_mail_read_pending = computed((): boolean => !this.has_mail_read());

  refresh(): void {
    this.refresh_tick.update((tick: number): number => tick + 1);
  }

  revoke(application: OAuthAuthorization): void {
    if (!this.can_manage() || !application.is_revocable || application.is_intranet_spa) {
      return;
    }

    this.revoking_client_id.set(application.client_id);
    this.api.revokeMyOAuthAuthorization(application.client_id).subscribe({
      next: () => {
        this.revoking_client_id.set(null);
        this.refresh();
      },
      error: () => this.revoking_client_id.set(null),
    });
  }

  logout(): void {
    if (!this.can_manage() || this.logging_out()) {
      return;
    }

    this.logging_out.set(true);
    this.auth.logout();
  }
}

interface OAuthScopeView {
  scope_id: string;
  label: string;
  is_granted: boolean;
  application_names_label: string | null;
}
